import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { Search, X, SlidersHorizontal, ArrowUpDown, RefreshCw, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNews, type SortOption, type DateRangeOption } from "@/context/NewsContext";
import { useDebounce } from "@/hooks/useDebounce";
import { cn } from "@/lib/utils";
import { useEasterEggCounter } from "@/hooks/useEasterEggCounter";
const SphereMode = lazy(() => import("./spheres/SphereMode"));
import { useSphereMode } from "@/hooks/useSphereMode";
import { useCategoryLabel, useT } from "@/i18n/useLocale";
import type { MessageKey } from "@/i18n/dictionaries";
import { T } from "@/i18n/T";




const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: "latest", label: "sort.latest" },
  { value: "trending", label: "sort.trending" },
  { value: "discussed", label: "sort.discussed" },
];

const DATE_RANGES: { value: DateRangeOption; label: string }[] = [
  { value: "all", label: "date.all" },
  { value: "today", label: "date.today" },
  { value: "3days", label: "date.3days" },
  { value: "week", label: "date.week" },
  { value: "2weeks", label: "date.2weeks" },
  { value: "month", label: "This month" },
  { value: "3months", label: "Last 3 months" },
];

const TOPIC_LABELS: Record<string, string> = {
  finance: "Finance",
  business: "Business",
  tech: "Technology",
  ai: "AI",
  robotics: "Robotics",
  space: "Space",
  biotech: "Biotech",
  security: "Cybersecurity",
  crypto: "Crypto",
  client: "Clients",
  clients: "Clients",
  general: "Other",
};

const TOPIC_ORDER = [
  "tech", "ai", "finance", "business", "security", "robotics", "space", "biotech", "crypto", "clients", "client", "general",
];

const FeedToolbar = () => {
  const t = useT();
  const categoryLabel = useCategoryLabel();
  const { filters, setFilter, availableSources, categoryCounts, allCachedArticles, refetch } = useNews();

  const { data: sourceTopics } = useQuery({
    queryKey: ["news-source-topics"],
    queryFn: async () => {
      const { data, error } = await supabase.from("news_sources").select("id, topic");
      if (error) throw error;
      return Object.fromEntries(
        (data ?? []).map((r: { id: string; topic: string | null }) => [r.id, r.topic ?? "general"])
      ) as Record<string, string>;
    },
    staleTime: 30 * 60 * 1000,
  });

  const groupedSources = (() => {
    const groups = new Map<string, { id: string; name: string }[]>();
    for (const s of availableSources) {
      const topic = sourceTopics?.[s.id] ?? "general";
      if (!groups.has(topic)) groups.set(topic, []);
      groups.get(topic)!.push(s);
    }
    return Array.from(groups.entries()).sort(
      (a, b) => (TOPIC_ORDER.indexOf(a[0]) + 1 || 99) - (TOPIC_ORDER.indexOf(b[0]) + 1 || 99)
    );
  })();

  const [searchInput, setSearchInput] = useState(filters.search);
  const debouncedInput = useDebounce(searchInput, 250);
  const railRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (debouncedInput !== filters.search) setFilter("search", debouncedInput);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedInput]);

  useEffect(() => {
    if (filters.search !== searchInput) setSearchInput(filters.search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  const activeCategory = filters.categories.length === 1 ? filters.categories[0] : "all";

  // Hidden easter egg: 16 rapid clicks on "All" turns the feed into physics spheres
  const { isActive: sphereMode, open: openSpheres, close: closeSpheres } = useSphereMode();
  const { registerClick, reset: resetEgg, showHint } = useEasterEggCounter(
    openSpheres,
    { target: 16, hintAt: 12 }
  );

  const handleCategory = (value: string) => {
    if (value === "all") {
      if (sphereMode) {
        closeSpheres();
      } else {
        registerClick();
      }
    } else {
      resetEgg();
    }
    setFilter("categories", value === "all" ? [] : [value]);
  };

  const toggleSource = (id: string) => {
    const next = filters.sourceIds.includes(id)
      ? filters.sourceIds.filter(s => s !== id)
      : [...filters.sourceIds, id];
    setFilter("sourceIds", next);
  };

  const advancedCount =
    filters.sourceIds.length + (filters.dateRange !== "all" ? 1 : 0);

  const currentSortLabel = SORT_OPTIONS.find(s => s.value === filters.sort)?.label ?? "sort.latest";

  return (
    <div className="sticky top-0 z-30 bg-background/80 backdrop-blur-md border-b border-border/60">
      <div className="pt-4 pb-3 space-y-3">
        {/* Search + actions row */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t("feed.searchPlaceholder")}
              className={cn(
                "pl-11 pr-10 h-11 rounded-full bg-muted/40 border-border/50 shadow-none",
                "focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:border-primary/40"
              )}
              aria-label={t("auto.search-articles-dd17")}
            />
            {searchInput && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSearchInput("")}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 h-7 w-7"
                aria-label={t("auto.clear-search-9983")}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>

          {/* Sort */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" aria-label={t("auto.sort-articles-83b1")} className="h-11 rounded-full gap-2 hidden sm:inline-flex">
                <ArrowUpDown className="h-4 w-4" />
                <span className="hidden md:inline">{t(currentSortLabel as MessageKey)}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuLabel>{t("feed.sortBy")}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {SORT_OPTIONS.map(opt => (
                <DropdownMenuItem
                  key={opt.value}
                  onClick={() => setFilter("sort", opt.value)}
                  className="justify-between"
                >
                  {t(opt.label as MessageKey)}
                  {filters.sort === opt.value && <Check className="h-4 w-4 text-primary" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Filters popover */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" aria-label={t("auto.filters-f3f4")} className="h-11 rounded-full gap-2 relative">
                <SlidersHorizontal className="h-4 w-4" />
                <span className="hidden md:inline">{t("feed.filters")}</span>
                {advancedCount > 0 && (
                  <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-xs">
                    {advancedCount}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-0">
              <div className="p-4 space-y-4">
                {/* Sort (mobile fallback) */}
                <div className="sm:hidden space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground"><T k="auto.sort-adc4" /></Label>
                  <div className="flex flex-wrap gap-2">
                    {SORT_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setFilter("sort", opt.value)}
                        className={cn(
                          "px-3 h-8 rounded-full text-sm border transition-colors",
                          filters.sort === opt.value
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-muted/40 border-border/50 hover:bg-muted"
                        )}
                      >
                        {t(opt.label as MessageKey)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {t("feed.dateRange")}
                  </Label>
                  <div className="grid grid-cols-2 gap-1.5">
                    {DATE_RANGES.map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setFilter("dateRange", opt.value)}
                        className={cn(
                          "px-3 h-8 rounded-md text-sm text-left transition-colors border",
                          filters.dateRange === opt.value
                            ? "bg-primary/10 text-primary border-primary/30"
                            : "bg-transparent border-border/50 hover:bg-muted"
                        )}
                      >
                        {t(opt.label as MessageKey)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      <T k="auto.sources-fb61" />
                    </Label>
                    {filters.sourceIds.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => setFilter("sourceIds", [])}
                      >
                        <T k="auto.reset-526d" />
                      </Button>
                    )}
                  </div>
                  {availableSources.length === 0 ? (
                    <p className="text-xs text-muted-foreground"><T k="auto.no-sources-available-yet-59f8" /></p>
                  ) : (
                    <ScrollArea className="h-56 pr-3">
                      <div className="space-y-3">
                        {groupedSources.map(([topic, sources]) => (
                          <div key={topic} className="space-y-1">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/80 px-0.5">
                              {TOPIC_LABELS[topic] ?? topic}
                            </p>
                            {sources.map(s => (
                              <label
                                key={s.id}
                                className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 rounded px-2 py-1.5 -mx-2"
                              >
                                <Checkbox
                                  checked={filters.sourceIds.includes(s.id)}
                                  onCheckedChange={() => toggleSource(s.id)}
                                />
                                <span className="truncate">{s.name}</span>
                              </label>
                            ))}
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </div>

                {(advancedCount > 0 || filters.sort !== "latest") && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      setFilter("sourceIds", []);
                      setFilter("dateRange", "all");
                      setFilter("sort", "latest");
                    }}
                  >
                    <T k="auto.reset-all-09a5" />
                  </Button>
                )}
              </div>
            </PopoverContent>
          </Popover>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => refetch()}
            className="h-11 w-11 rounded-full hidden sm:inline-flex"
            aria-label={t("auto.refresh-news-322e")}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        {/* Category chip rail, real categories from articles */}
        {categoryCounts.length > 0 && (
          <div className="relative">
            <div
              ref={railRef}
              className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none snap-x"
              style={{ scrollbarWidth: "none" }}
            >
              {categoryCounts.filter(cat => cat.value === "all" || cat.count > 0).map(cat => {
                const active = activeCategory === cat.value || (cat.value !== "all" && activeCategory.toLowerCase() === cat.value);
                return (
                  <button
                    key={cat.value}
                    onClick={() => handleCategory(cat.value === "all" ? "all" : cat.label)}
                    className={cn(
                      "shrink-0 snap-start h-8 px-4 rounded-full text-sm font-medium transition-colors border inline-flex items-center gap-1.5",
                      active
                        ? "bg-primary text-primary-foreground border-primary shadow-xs"
                        : "bg-muted/40 text-muted-foreground border-transparent hover:bg-muted hover:text-foreground",
                      cat.value === "all" && showHint && "animate-pulse ring-2 ring-primary/40"
                    )}
                  >
                    <span>{categoryLabel(cat.label)}</span>
                    <span className={cn(
                      "text-xs tabular-nums",
                      active ? "text-primary-foreground" : "text-muted-foreground"
                    )}>
                      {cat.count}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-background to-transparent" />
          </div>
        )}
      </div>
      {sphereMode && (
        <Suspense fallback={null}>
          <SphereMode onClose={closeSpheres} />
        </Suspense>
      )}
    </div>
  );
};

export default FeedToolbar;
